import { useCallback } from 'react';
import { useApi } from '@hooks/services/api';
import { useAuth } from '@providers/authProvider';
import { extractErrorMessage } from '@hooks/services/_useBaseService';

export const useUserService = () => {
    const { apiRequest } = useApi();
    const { user, setUser } = useAuth();

    /**
     * Handle API error responses
     * @param {Error} error - The error object
     * @returns {Error} - A formatted error
     */
    const handleError = useCallback((error) => {
        const serviceError = new Error(extractErrorMessage(error));
        serviceError.code = error?.meta?.error?.code || error?.response?.data?.meta?.error?.code || error?.code;
        serviceError.statusCode = error?.status || error?.response?.status;
        serviceError.details = error?.meta?.error?.details || error?.response?.data?.meta?.error?.details || error?.details;
        return serviceError;
    }, []);

    /**
     * Get the current user's information
     * @param {boolean} forceRefresh - Whether to force a refresh from API
     * @returns {Promise<Object>} - The user data
     */
    const getUser = useCallback(async (forceRefresh = false) => {
        try {
            // If user exists in auth context and no force refresh, return it
            if (user && !forceRefresh) {
                return user;
            }

            // Otherwise, fetch from API
            const response = await apiRequest('/users/me', 'GET');

            // Handle array response - API returns data as array
            const userData = Array.isArray(response.data) ? response.data[0] : response.data;

            // Update auth provider state (which also updates localStorage)
            setUser(userData);

            return userData;
        } catch (error) {
            throw handleError(error);
        }
    }, [user, apiRequest, handleError, setUser]);

    /**
     * Get full user settings from API (includes sensitive fields)
     * Does NOT update auth context to avoid storing sensitive data in localStorage
     * @returns {Promise<Object>} - The full user data including sensitive fields
     */
    const getUserSettings = useCallback(async () => {
        try {
            const response = await apiRequest('/users/me', 'GET');
            // Handle array response - API returns data as array
            return Array.isArray(response.data) ? response.data[0] : response.data;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Update the current user's information
     * @param {Object} userData - Updated user data
     * @returns {Promise<Object>} - The updated user data
     */
    const updateUser = useCallback(async (userData) => {
        try {
            const response = await apiRequest('/users/me', 'PUT', userData);

            // Handle array response
            const updatedUser = Array.isArray(response.data) ? response.data[0] : response.data;

            // Merge existing user data with updates
            const newUser = {
                ...user,
                ...updatedUser
            };

            // Update auth provider state (which also updates localStorage)
            setUser(newUser);

            // Return the complete updated user data
            return newUser;
        } catch (error) {
            throw handleError(error);
        }
    }, [user, apiRequest, handleError, setUser]);

    /**
     * Delete a user
     * @param {string|number} userId - The ID of the user to delete
     * @returns {Promise<boolean>} - Success status
     */
    const deleteUser = useCallback(async (userId) => {
        try {
            const response = await apiRequest(`/users/${userId}`, 'DELETE');
            return response.data;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Reprice inventory items
     * @param {number[]|null} itemIds - Optional selected inventory item ids for immediate repricing
     * @returns {Promise<Object>} - The reprice results
     */
    const repriceInventory = useCallback(async (itemIds = null) => {
        try {
            const payload = {
                ...(Array.isArray(itemIds) ? { item_ids: itemIds } : {}),
                bricklink_refresh_item_price: true,
                bricklink_refresh_lot_price: true
            };

            const response = await apiRequest('/inventoryItems/reprice', 'POST', payload);
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    const preflightRepriceInventory = useCallback(async (itemIds = null) => {
        try {
            const payload = {
                ...(Array.isArray(itemIds) ? { item_ids: itemIds } : {}),
                bricklink_refresh_item_price: true,
                bricklink_refresh_lot_price: true
            };

            const response = await apiRequest('/inventoryItems/reprice-preflight', 'POST', payload);
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    const preflightPercentageRepriceInventory = useCallback(async (itemIds) => {
        try {
            if (!Array.isArray(itemIds) || itemIds.length === 0) {
                throw new Error('Percentage repricing requires selected inventory item ids');
            }
            const payload = {
                item_ids: itemIds
            };

            const response = await apiRequest('/inventoryItems/reprice-percentage-preflight', 'POST', payload);
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    const percentageRepriceInventory = useCallback(async (itemIds, adjustment) => {
        try {
            if (!Array.isArray(itemIds) || itemIds.length === 0) {
                throw new Error('Percentage repricing requires selected inventory item ids');
            }
            if (!adjustment || typeof adjustment !== 'object') {
                throw new Error('Percentage repricing adjustment is required');
            }
            const payload = {
                item_ids: itemIds,
                increase_percent: adjustment.increase_percent,
                decrease_percent: adjustment.decrease_percent
            };

            const response = await apiRequest('/inventoryItems/reprice-percentage', 'POST', payload);
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Query jobs with optional filters
     * @param {Object} params - Query params (active, job_type)
     * @returns {Promise<Object>} - The jobs response
     */
    const getJobs = useCallback(async (params = {}) => {
        try {
            const queryString = new URLSearchParams(params).toString();
            const url = queryString ? `/jobs?${queryString}` : '/jobs';
            const response = await apiRequest(url, 'GET');
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Get repricing job status by job id
     * @param {string} jobId - Repricing job id
     * @returns {Promise<Object>} - The job status response
     */
    const getRepriceJobStatus = useCallback(async (jobId) => {
        try {
            const response = await apiRequest(`/inventoryItems/reprice/${jobId}`, 'GET');
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Cancel repricing job by job id
     * @param {string} jobId - Repricing job id
     * @returns {Promise<Object>} - The cancelled job response
     */
    const cancelRepriceJob = useCallback(async (jobId) => {
        try {
            const response = await apiRequest(`/inventoryItems/reprice/${jobId}/cancel`, 'POST');
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Retry a failed repricing job by job id
     * @param {string} jobId - Repricing job id
     * @returns {Promise<Object>} - The queued retry job response
     */
    const retryRepriceJob = useCallback(async (jobId) => {
        try {
            const response = await apiRequest(`/inventoryItems/reprice/${jobId}/retry`, 'POST');
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Apply tier pricing defaults to all inventory items
     * @returns {Promise<Object>} - The retier results
     */
    const retierInventory = useCallback(async () => {
        try {
            const response = await apiRequest('/inventoryItems/retier', 'POST');
            return response;
        } catch (error) {
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    return {
        getUser,
        getUserSettings,
        updateUser,
        deleteUser,
        repriceInventory,
        preflightRepriceInventory,
        preflightPercentageRepriceInventory,
        percentageRepriceInventory,
        getJobs,
        getRepriceJobStatus,
        cancelRepriceJob,
        retryRepriceJob,
        retierInventory
    };
}; 
